import { parseISO } from 'date-fns';
import type { ICommentOptions, ICommentsOptions } from 'docx';
import { CommentRangeEnd, CommentRangeStart, CommentReference, Paragraph, TextRun } from 'docx';
import { uniqBy } from 'ramda';
import { Point, Range } from 'slate';
import type { Editor, Path } from 'slate';

import type {
  DBComment,
  DocumentContext,
  DOCXInline,
  DOCXTextRun,
  ParentDBComment,
} from './internal';
import type { AlineText } from '../../../slate-api/internal';
import { Queries } from '../../../slate-api/internal';

export type CommentId = string;
export type CommentInfo = {
  author: string;
  date: Date;
  docXCommentId: number;
  ranges: Range[];
  replies: Omit<CommentInfo, 'ranges' | 'replies'>[];
  resolved: boolean;
  text: string;
  parentId?: number;
};
export type CommentMap = Record<CommentId, CommentInfo>;
export type DOCXCommentRange = CommentRangeStart | CommentRangeEnd;

/**
 * Wraps text with DOCX comment markers for both parent comments and their replies
 */
export function surroundTextWithComments(
  textEntry: [Partial<AlineText>, Path],
  options: {
    context: DocumentContext;
    textRun: DOCXTextRun;
  }
): DOCXInline[] {
  const { context, textRun } = options;
  const [textNode, textPath] = textEntry;

  if (!textNode.comments?.length) return [textRun];

  const textRange: Range = {
    anchor: { offset: 0, path: textPath },
    focus: { offset: textNode.text?.length ?? 0, path: textPath },
  };
  const commentRangeStarts = createCommentRangeStarts(textNode, textRange, context);
  const commentRangeEnds = createCommentRangeEnds(textNode, textRange, context);

  return [...commentRangeStarts, textRun, ...commentRangeEnds];
}

/**
 * Creates CommentRangeStart markers for comments that begin in this text range
 */
function createCommentRangeStarts(
  textNode: Partial<AlineText>,
  textRange: Range,
  context: DocumentContext
): CommentRangeStart[] {
  return (
    textNode.comments?.flatMap((commentId): CommentRangeStart[] => {
      const commentInfo = commentId in context.commentMap ? context.commentMap[commentId] : null;
      if (!commentInfo || !commentInfo.ranges.length) return [];

      const [textStart] = Range.edges(textRange);
      const beginsARange = commentInfo.ranges.some(range =>
        Point.equals(Range.start(range), textStart)
      );
      if (!beginsARange) return [];

      return [
        new CommentRangeStart(commentInfo.docXCommentId),
        ...commentInfo.replies.map(replyInfo => new CommentRangeStart(replyInfo.docXCommentId)),
      ];
    }) ?? []
  );
}

/**
 * Creates CommentRangeEnd markers and references for comments that end in this text range
 */
function createCommentRangeEnds(
  textNode: Partial<AlineText>,
  textRange: Range,
  context: DocumentContext
): (CommentRangeEnd | TextRun)[] {
  return (
    textNode.comments?.flatMap((commentId): (CommentRangeEnd | TextRun)[] => {
      const commentInfo = commentId in context.commentMap ? context.commentMap[commentId] : null;
      if (!commentInfo || !commentInfo.ranges.length) return [];

      const [, textEnd] = Range.edges(textRange);
      const endsARange = commentInfo.ranges.some(range => Point.equals(Range.end(range), textEnd));
      if (!endsARange) return [];

      const rangeEnds = commentInfo.ranges.map(range => Range.edges(range)[1]);
      const latestEnd = rangeEnds.reduce((latest, end) =>
        Point.isAfter(end, latest) ? end : latest
      );
      const isFinalRun = Point.equals(latestEnd, textEnd);

      return [
        new CommentRangeEnd(commentInfo.docXCommentId),
        ...(isFinalRun
          ? [new TextRun({ children: [new CommentReference(commentInfo.docXCommentId)] })]
          : []),
        ...commentInfo.replies.flatMap(replyInfo => [
          new CommentRangeEnd(replyInfo.docXCommentId),
          ...(isFinalRun
            ? [new TextRun({ children: [new CommentReference(replyInfo.docXCommentId)] })]
            : []),
        ]),
      ];
    }) ?? []
  );
}

export interface DocxComments {
  commentMap: CommentMap;
  docXComments: ICommentsOptions;
}

/**
 * Gets the display name for a comment author
 */
function getAuthorName(comment: DBComment, authorNames: Map<string, string>): string {
  return comment.importedAuthorName ?? authorNames.get(comment.authorId ?? '') ?? 'Anonymous';
}

/**
 * Processes a parent comment and its replies, adding them to commentMap and docXComments
 */
function processCommentWithReplies(args: {
  authorNames: Map<string, string>;
  parentComment: ParentDBComment;
  ranges: Range[];
  replies: DBComment[];
  startingCommentId: number;
}): {
  commentMap: Record<string, CommentInfo>;
  docXCommentOptionsList: ICommentOptions[];
  nextCommentId: number;
} {
  const { authorNames, parentComment, ranges, replies, startingCommentId } = args;
  const docXCommentOptionsList: ICommentOptions[] = [];
  const commentMap: Record<string, CommentInfo> = {};
  const repliesForComment = replies.filter(reply => reply.parentCommentId === parentComment.id);

  const commentInfo: CommentInfo = {
    author: getAuthorName(parentComment, authorNames),
    date: parseISO(parentComment.createdAt),
    docXCommentId: startingCommentId,
    ranges,
    replies: repliesForComment.map(
      (reply, index): Omit<CommentInfo, 'ranges' | 'replies'> => ({
        author: getAuthorName(reply, authorNames),
        date: parseISO(reply.createdAt),
        docXCommentId: startingCommentId + index + 1,
        parentId: startingCommentId,
        // The reply inherits resolved status from the parent comment.
        resolved: parentComment.resolved,
        text: reply.text,
      })
    ),
    resolved: parentComment.resolved,
    text: parentComment.text,
  };

  commentMap[parentComment.nodeRefId] = commentInfo;
  docXCommentOptionsList.push({
    author: commentInfo.author,
    children: [new Paragraph({ children: [new TextRun({ text: commentInfo.text })] })],
    date: commentInfo.date,
    id: commentInfo.docXCommentId,
    resolved: commentInfo.resolved,
  });
  commentInfo.replies.forEach(reply => {
    docXCommentOptionsList.push({
      author: reply.author,
      children: [new Paragraph({ children: [new TextRun({ text: reply.text })] })],
      date: reply.date,
      id: reply.docXCommentId,
      parentId: reply.parentId,
      resolved: commentInfo.resolved,
    });
  });

  return {
    commentMap,
    docXCommentOptionsList,
    nextCommentId: commentInfo.docXCommentId + commentInfo.replies.length,
  };
}

export function getIsParentComment(comment: DBComment | null): comment is ParentDBComment {
  return Boolean(comment?.nodeRefId);
}

/**
 * Creates DOCX comments structure from editor state and database comments
 */
export function createDocxComments(
  editor: Editor,
  options: { authorNames: Map<string, string>; dbComments: DBComment[] }
): DocxComments {
  const { authorNames, dbComments } = options;
  const uniqComments = uniqBy(dbComment => dbComment.id, dbComments);
  const { commentRanges, uniqCommentIds } = Queries.getCommentRanges(editor, { at: [] });
  const nodeIdToDBComment = uniqComments.reduce<Record<string, DBComment>>((acc, dbComment) => {
    if (dbComment.nodeRefId) acc[dbComment.nodeRefId] = dbComment;
    return acc;
  }, {});
  const replies = uniqComments.filter(dbComment => Boolean(dbComment.parentCommentId));

  // Process all comments attached to text and their replies
  return uniqCommentIds.reduce<DocxComments & { lastCommentId: number }>(
    (acc, commentNodeRefId) => {
      const { lastCommentId } = acc;
      const { ranges = [] } = commentRanges[commentNodeRefId] ?? {};
      const parentComment =
        commentNodeRefId in nodeIdToDBComment ? nodeIdToDBComment[commentNodeRefId] : null;

      // Skip if not a parent comment or no ranges.
      if (!getIsParentComment(parentComment) || !ranges.length) {
        return acc;
      }

      const {
        commentMap: commentAndRepliesMap,
        docXCommentOptionsList,
        nextCommentId,
      } = processCommentWithReplies({
        authorNames,
        parentComment,
        ranges,
        replies,
        // DOCX uses integer-based comment IDs, so we need to increment the last comment ID.
        startingCommentId: lastCommentId + 1,
      });

      return {
        commentMap: { ...acc.commentMap, ...commentAndRepliesMap },
        docXComments: { children: [...acc.docXComments.children, ...docXCommentOptionsList] },
        lastCommentId: nextCommentId,
      };
    },
    {
      commentMap: {},
      docXComments: { children: [] },
      lastCommentId: 0,
    }
  );
}
